import React from 'react';
import { Sparkles } from 'lucide-react';
import InfoHint from './InfoHint';
import type { MediaAlias } from '../../services/studioService';

interface Props {
  aliases: MediaAlias[];
  value: string;
  onChange: (key: string) => void;
}

/**
 * MediaAliasPicker — the "which look?" video model chooser. The user picks an OUTCOME (Everyday / Cinematic /
 * Dynamic / Social), never a model name; the real model + price live server-side. A smart Recommended default,
 * a one-line description + an agent tip on the selection, and the per-clip credit cost so the trade-off is honest.
 */
const MediaAliasPicker: React.FC<Props> = ({ aliases, value, onChange }) => {
  if (!aliases.length) return null;
  const selected = aliases.find((a) => a.key === value) || aliases.find((a) => a.recommended) || aliases[0];
  return (
    <div className="mt-4">
      <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-secondary-400 dark:text-secondary-500">Video look <InfoHint text="How your clips are generated — a trade-off of quality, style, and credit cost. Not sure? Leave it on Recommended." /></div>
      <div className="flex flex-wrap gap-1.5">
        {aliases.map((a) => {
          const on = a.key === selected.key;
          return (
            <button
              key={a.key}
              type="button"
              title={a.description}
              onClick={() => onChange(a.key)}
              className={on
                ? 'rounded-full border border-secondary-900 dark:border-white bg-secondary-100 dark:bg-secondary-800 px-3 py-1.5 text-xs font-semibold text-secondary-900 dark:text-white'
                : 'rounded-full border border-secondary-200 dark:border-secondary-700 px-3 py-1.5 text-xs text-secondary-600 dark:text-secondary-300 hover:border-secondary-400'}
            >
              {a.label}{a.recommended ? ' · Recommended' : ''}
            </button>
          );
        })}
      </div>
      <div className="mt-2 flex items-start gap-1.5 rounded-lg bg-secondary-50 dark:bg-secondary-800/40 p-2 text-[11px] text-secondary-500 dark:text-secondary-400">
        <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <div>
          <span className="text-secondary-600 dark:text-secondary-300">{selected.description}</span>{' '}
          <span>{selected.tip}</span>
          <div className="mt-0.5 text-secondary-400 dark:text-secondary-500">~{selected.creditCost.toLocaleString()} {selected.currency} per clip</div>
        </div>
      </div>
    </div>
  );
};

export default MediaAliasPicker;
